import { pipe } from 'fp-ts/lib/function';
import { map } from 'fp-ts/lib/TaskEither';
import { getDownloadToken } from '@/services/api/auth';
import {
  encodeInspectionQuery,
  encodeIssueQuery,
  encodeQuestionQuery,
  InspectionQuery,
  IssueQuery,
  QuestionQuery,
} from './query';
import { normalizePath, Query, string, stringify } from '../../request';
import { GroupByDates, InspectionGroupBy, IssueGroupBy, QuestionGroupBy } from './types';

export type SupportedExportFormat = 'csv' | 'xls' | 'pdf' | 'png' | 'jpeg';
export const offlineFormatsWhiteList: Array<SupportedExportFormat> = ['pdf', 'jpeg', 'png'];
export const supportedMimeTypes: Record<SupportedExportFormat, string> = {
  csv: 'text/csv',
  xls: 'application/vnd.ms-excel',
  pdf: 'application/pdf',
  png: 'image/png',
  jpeg: 'image/jpeg',
};

const baseUrl = '/v2/analytics';

const createDownloadUrl = (
  url: string,
  query: Query,
  format: string,
  locale: string,
) => (
  token: string,
) => {
  const u = new URL(normalizePath(`${url}.${format}`));
  u.search = stringify({
    ...query,
    token: string(token),
    locale: string(locale),
  });
  return u;
};

// inspections
export const inspectionListExportUrl = (
  query: InspectionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/inspections`,
    encodeInspectionQuery(query),
    format,
    locale,
  )),
);
export const groupedInspectionListExportUrl = (
  groupBy: InspectionGroupBy,
  query: InspectionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/inspections/by${groupBy}`,
    encodeInspectionQuery(query),
    format,
    locale,
  )),
);

// issues
export const issueListExportUrl = (
  query: IssueQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/issues`,
    encodeIssueQuery(query),
    format,
    locale,
  )),
);
export const groupedIssueListExportUrl = (
  groupBy: IssueGroupBy | GroupByDates,
  query: IssueQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/issues/by${groupBy}`,
    encodeIssueQuery(query),
    format,
    locale,
  )),
);

// questions (negative responses)
export const negativeResponsesListExportUrl = (
  query: QuestionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/questions/negative-answers`,
    encodeQuestionQuery(query),
    format,
    locale,
  )),
);
export const groupedNegativeResponsesListExportUrl = (
  groupBy: QuestionGroupBy,
  query: QuestionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/questions/by${groupBy}-negative-responses`,
    encodeQuestionQuery(query),
    format,
    locale,
  )),
);

// questions (scores)
export const scoresListExportUrl = (
  query: QuestionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/questions/scores`,
    encodeQuestionQuery(query),
    format,
    locale,
  )),
);

export const groupedScoresListExportUrl = (
  groupBy: QuestionGroupBy,
  query: QuestionQuery,
  format: SupportedExportFormat,
  locale: string,
) => pipe(
  getDownloadToken(),
  map(createDownloadUrl(
    `${baseUrl}/questions/by${groupBy}-scores`,
    encodeQuestionQuery(query),
    format,
    locale,
  )),
);
